import type { RotaryKilnMeasurement } from '../../../types/reports.js';

/**
 * Feldkataloge des Drehofens -- reines Modul, KEIN React-Import (Plan 28-05). Verschoben aus
 * `KilnForm.tsx`, wo sie ursprünglich standen, wörtlich samt Kommentaren: `previousValuesSource.ts`
 * ist ebenfalls ein reines Modul und darf keine Komponentendatei importieren, braucht aber
 * `ALL_KILN_ANNUAL_FIELD_KEYS`/`ALL_EMISSION_FIELD_KEYS` für die Vorjahreswerte-Umsortierung
 * (D-15/D-16). `KilnForm.tsx` importiert die vier Exporte von hier und reicht sie unveraendert
 * re-exportiert weiter, damit `KilnAnnualValuesForm`, `KilnMeasurementCard`,
 * `DrehofenSection` und `createEmptyMeasurement` ohne Aenderung weiterfunktionieren.
 */

// Jahresmittelwerte am Ofen (D-01), neun Felder flach, alle mg/m³. `dual: true` kennzeichnet HCl
// und HF -- die beiden Komponenten, die es zusaetzlich je Einzelmessung gibt (D-02) und die daher
// am Ofen ein zusaetzliches Haekchen "diskontinuierlich gemessen" tragen. Gerendert wird dieser
// Katalog in Plan 27.1-07 (Ofenkarte); hier nur deklariert, damit der Ofenkatalog von Anfang an aus
// derselben Quelle stammt wie der Messungskatalog.
export const KILN_ANNUAL_FIELDS = [
  { key: 'dust', label: 'Staub' },
  { key: 'nox', label: 'NOₓ' },
  { key: 'so2', label: 'SO₂' },
  { key: 'hcl', label: 'HCl', dual: true },
  { key: 'hf', label: 'HF', dual: true },
  { key: 'co', label: 'CO' },
  { key: 'nh3', label: 'NH₃' },
  { key: 'mercury', label: 'Quecksilber (Hg)' },
  { key: 'toc', label: 'TOC' },
] as const;

// Alle neun Ofen-Wertschluessel (ohne "Value"-Suffix), aus KILN_ANNUAL_FIELDS abgeleitet.
export const ALL_KILN_ANNUAL_FIELD_KEYS: string[] = KILN_ANNUAL_FIELDS.map((field) => field.key);

// Emissionsfeld-Katalog der Einzelmessung (D-03), drei Gruppen mit zusammen 26 Feldern. Gegenueber
// dem Stand vor Phase 27.1 heisst die erste Gruppe "Chlor- und Fluorverbindungen" statt "Staub und
// Gase" und behaelt nur HCl/HF -- Staub und die uebrigen Gase sind an den Ofen gewandert (D-01);
// Quecksilber faellt aus den Schwermetallen (D-01), Methan (D-05) und TOC (D-01) aus den
// organischen Verbindungen. Exportiert, damit createEmptyMeasurement seine 26 Wertschluessel
// hieraus ableitet statt sie erneut aufzuzaehlen (Lehre aus 18-08: doppelt gepflegte Feldkataloge
// laufen auseinander).
export const MEASUREMENT_EMISSION_FIELDS = {
  chlorFluor: {
    title: 'Chlor- und Fluorverbindungen',
    unit: 'mg/m³',
    fields: [
      { key: 'hcl', label: 'HCl' },
      { key: 'hf', label: 'HF' },
    ],
  },
  heavyMetals: {
    title: 'Schwermetalle',
    unit: 'mg/m³',
    fields: [
      { key: 'cadmium', label: 'Cadmium (Cd)' },
      { key: 'thallium', label: 'Thallium (Tl)' },
      { key: 'antimony', label: 'Antimon (Sb)' },
      { key: 'arsenic', label: 'Arsen (As)' },
      { key: 'lead', label: 'Blei (Pb)' },
      { key: 'chromium', label: 'Chrom (Cr)' },
      { key: 'cobalt', label: 'Kobalt (Co)' },
      { key: 'copper', label: 'Kupfer (Cu)' },
      { key: 'manganese', label: 'Mangan (Mn)' },
      { key: 'nickel', label: 'Nickel (Ni)' },
      { key: 'vanadium', label: 'Vanadium (V)' },
      { key: 'tin', label: 'Zinn (Sn)' },
      { key: 'beryllium', label: 'Beryllium (Be)' },
      { key: 'selenium', label: 'Selen (Se)' },
      { key: 'tellurium', label: 'Tellur (Te)' },
      { key: 'zinc', label: 'Zink (Zn)' },
    ],
  },
  organicCompounds: {
    title: 'Organische Verbindungen',
    fields: [
      { key: 'pcbDin', label: 'PCB (DIN)', unit: 'ng/m³' },
      { key: 'pcbWho', label: 'PCB (WHO-TEQ)', unit: 'ngTE/m³' },
      { key: 'pcddF', label: 'PCDD/F', unit: 'ngTE/m³' },
      { key: 'pah', label: 'PAK', unit: 'µg/m³' },
      { key: 'btex', label: 'BTEX', unit: 'mg/m³' },
      { key: 'benzene', label: 'Benzol', unit: 'mg/m³' },
      { key: 'formaldehyde', label: 'Formaldehyd', unit: 'mg/m³' },
      { key: 'benzoPyrene', label: 'Benzo(a)pyren', unit: 'µg/m³' },
    ],
  },
};

// Alle 26 Wertschluessel (ohne "Value"-Suffix) der Einzelmessung, aus MEASUREMENT_EMISSION_FIELDS
// abgeleitet. Exportiert (Plan 19-09), damit measurementErrorSig in KilnMeasurementCard.tsx
// dieselbe Liste fuer die Memoisierungs-Fehlersignatur verwendet statt sie ein zweites Mal
// aufzuzaehlen (Lehre aus 18-08, verschaerft durch den dokumentierten Vorfall T-19-27/T-19-28: eine
// unvollstaendige Ableitung macht Backend-Fehler auf den fehlenden Feldern unsichtbar).
export const ALL_EMISSION_FIELD_KEYS: string[] = Object.values(
  MEASUREMENT_EMISSION_FIELDS,
).flatMap((group) => group.fields.map((field) => field.key));

/**
 * Liefert eine frisch angelegte, leere Messung (D-13/D-17): measurementDate, oxygenReference und
 * valueBasis sowie alle 26 `<key>Value`- plus 26 `<key>BelowLimit`-Emissionsfelder. `valueBasis`
 * bekommt ausdruecklich `null` und keinen anderen Vorbelegungswert: ein Nicht-Null-Default wuerde
 * hasAnyData() im Backend jede frisch angelegte, sonst leere Messung faelschlich als "traegt
 * Daten" melden und einen neu angelegten Ofen damit nicht mehr einreichbar machen (Phase 19,
 * D-16). Wird sowohl beim Anlegen eines neuen Ofens (genau eine Messung, D-13) als auch beim
 * Anlegen einer weiteren Messung (D-17, niemals durch Kopieren) verwendet.
 */
export function createEmptyMeasurement(): RotaryKilnMeasurement {
  const emissionFields = Object.fromEntries(
    ALL_EMISSION_FIELD_KEYS.flatMap((key) => [
      [`${key}Value`, null],
      [`${key}BelowLimit`, false],
    ]),
  );
  return {
    measurementDate: null,
    oxygenReference: null,
    valueBasis: null,
    ...emissionFields,
  } as RotaryKilnMeasurement;
}

// ==================== Frachtenberechnung (Phase 38, D-01/D-11) ====================
// Das "Rechenblatt" ist ein optionales Hilfsmittel je Drehofen -- kein Meldefeld. Property-Namen
// (compound*/direct*, <key>Value) sind wortgleich mit der Backend-Struktur aus 38-01/38-02
// (RotaryKilnLoadCalculation, KilnLoadFieldCatalog): rotaryKilns.{i}.loadCalculation.{feld}
// (RESEARCH.md Pitfall 5 -- API-Struktur zuerst, Frontend-Pfade exakt danach ausgerichtet).

// Betriebsdaten-Tabelle des Rechenblatts (D-07, Sketch 021 A+): drei Zeilen (Groesse), zwei
// Spalten (Betriebsart) -- Verbund- (compound*, faktische Mindestangabe) und Direktbetrieb
// (direct*, optional, D-14 verlangt trotzdem keine Pflichtfeldregel).
export const KILN_LOAD_OPERATING_ROWS = [
  {
    label: 'Volumenstrom bei tatsächlichem O₂',
    unit: 'm³/h',
    compoundKey: 'compoundFlow',
    directKey: 'directFlow',
  },
  {
    label: 'O₂-Gehalt',
    unit: 'Vol.-%',
    compoundKey: 'compoundOxygen',
    directKey: 'directOxygen',
  },
  {
    label: 'Betriebsstunden',
    unit: 'h',
    compoundKey: 'compoundHours',
    directKey: 'directHours',
  },
] as const;

// Alle sechs Betriebsdaten-Property-Namen, aus KILN_LOAD_OPERATING_ROWS abgeleitet -- erst die
// drei Verbundbetrieb-, dann die drei Direktbetrieb-Schluessel (Reihenfolge wortgleich mit
// KilnLoadFieldCatalog::OPERATING_FIELDS, Backend), damit createEmptyLoadCalculation() sie ohne
// erneute Aufzaehlung befuellen kann.
export const ALL_KILN_LOAD_OPERATING_KEYS: string[] = [
  ...KILN_LOAD_OPERATING_ROWS.map((row) => row.compoundKey),
  ...KILN_LOAD_OPERATING_ROWS.map((row) => row.directKey),
];

// Die 33 Frachten-Komponenten als Vereinigung von KILN_ANNUAL_FIELDS (ohne die HCl/HF-Dopplung)
// und MEASUREMENT_EMISSION_FIELDS (D-11) -- ABGELEITET, nicht neu aufgezaehlt: eine zweite,
// handgepflegte Komponentenliste ist in diesem Projekt bereits zweimal auseinandergelaufen
// (18-08, T-19-27/28). Die Gegenprobe gegen den Backend-Katalog (KilnLoadFieldCatalog) fuehrt
// KilnLoadFieldCatalogTest (38-01); dieser Katalog hier ist die Frontend-Quelle davon.
const KILN_LOAD_CONTINUOUS_FIELDS = KILN_ANNUAL_FIELDS.filter(
  (field) => !('dual' in field && field.dual),
);

// Feldeinheit (D-13) der beiden Massenstrom-Mittelwerte (<key>MassFlowCompound/<key>MassFlowDirect)
// je Konzentrationseinheit -- Backend-Vertrag aus 40-01/40-03 (KilnLoadFieldCatalog::COMPONENTS,
// Schluessel 'massFlowUnit'). Literales Objekt statt abgeleitet: es IST die Quelle, aus der die
// massFlowUnit-Eigenschaft unten an den diskontinuierlichen Feldern berechnet wird, und die
// einzige Stelle, die der Backend-Synctest (KilnLoadFieldCatalogTest::
// testMassFlowUnitByConcentrationUnitMatchesFrontendCatalog) als Text liest.
export const MASS_FLOW_UNIT_BY_CONCENTRATION_UNIT: Record<string, string> = {
  'mg/m³': 'g/h',
  'µg/m³': 'mg/h',
  'ng/m³': 'mg/h',
  'ngTE/m³': 'µg TE/h',
};

export const KILN_LOAD_COMPONENT_GROUPS = [
  {
    key: 'continuous',
    title: 'Kontinuierlich gemessen',
    fields: KILN_LOAD_CONTINUOUS_FIELDS.map((field) => ({
      key: field.key,
      label: field.label,
      unit: 'mg/m³',
    })),
  },
  ...(
    Object.entries(MEASUREMENT_EMISSION_FIELDS) as [
      string,
      { title: string; unit?: string; fields: { key: string; label: string; unit?: string }[] },
    ][]
  ).map(([key, group]) => ({
    key,
    title: group.title,
    // Phase 40 (D-06/D-13): jedes Feld dieser drei Gruppen ist diskontinuierlich und bekommt
    // zusaetzlich massFlowUnit, abgeleitet aus der eigenen Konzentrationseinheit ueber
    // MASS_FLOW_UNIT_BY_CONCENTRATION_UNIT -- keine zweite handgetippte Zuordnung je Komponente.
    fields: group.fields.map((field) => {
      const unit = field.unit ?? group.unit ?? '';
      return {
        key: field.key,
        label: field.label,
        unit,
        massFlowUnit: MASS_FLOW_UNIT_BY_CONCENTRATION_UNIT[unit],
      };
    }),
  })),
];

// Alle 33 Komponentenschluessel (ohne "Value"-Suffix) in Katalogreihenfolge, aus
// KILN_LOAD_COMPONENT_GROUPS abgeleitet -- Muster ALL_EMISSION_FIELD_KEYS oben.
export const KILN_LOAD_COMPONENT_KEYS: string[] = KILN_LOAD_COMPONENT_GROUPS.flatMap((group) =>
  group.fields.map((field) => field.key),
);

// Phase 40 (D-06/D-09): die kontinuierliche Gruppe (7 Felder, ausschliesslich Konzentration) und
// die drei diskontinuierlichen Gruppen (26 Felder, wahlweise Konzentration ODER Massenstrom) --
// beide ueber .filter() aus KILN_LOAD_COMPONENT_GROUPS abgeleitet, keine zweite Gruppierung.
export const KILN_LOAD_CONTINUOUS_GROUPS = KILN_LOAD_COMPONENT_GROUPS.filter(
  (group) => group.key === 'continuous',
);
export const KILN_LOAD_DISCONTINUOUS_GROUPS = KILN_LOAD_COMPONENT_GROUPS.filter(
  (group) => group.key !== 'continuous',
);

// Alle 26 diskontinuierlichen Komponentenschluessel in Katalogreihenfolge (chlorFluor,
// heavyMetals, organicCompounds -- dieselbe Reihenfolge wie
// KilnLoadFieldCatalog::discontinuousComponentKeys() im Backend).
export const KILN_LOAD_DISCONTINUOUS_KEYS: string[] = KILN_LOAD_DISCONTINUOUS_GROUPS.flatMap(
  (group) => group.fields.map((field) => field.key),
);

// Liefert den Property-Namen des Massenstrom-Mittelwerts Verbundbetrieb/Direktbetrieb, z.B.
// `hcl` -> `hclMassFlowCompound`/`hclMassFlowDirect` (Phase 40, D-06, wortgleich mit
// KilnLoadFieldCatalog::massFlowCompoundPropertyFor()/massFlowDirectPropertyFor()).
export function massFlowCompoundKey(key: string): string {
  return `${key}MassFlowCompound`;
}

export function massFlowDirectKey(key: string): string {
  return `${key}MassFlowDirect`;
}

// Alle 52 Massenstrom-Property-Namen: erst alle 26 Compound-, dann alle 26 Direct-Schluessel, je
// in Katalogreihenfolge (D-06) -- Reihenfolge wortgleich mit dem Backend-Vertrag aus 40-01.
export const ALL_KILN_LOAD_MASS_FLOW_KEYS: string[] = [
  ...KILN_LOAD_DISCONTINUOUS_KEYS.map(massFlowCompoundKey),
  ...KILN_LOAD_DISCONTINUOUS_KEYS.map(massFlowDirectKey),
];

// Rechengrundlage der diskontinuierlichen Zeilen (Phase 40, D-09): Konzentration x gewichteter
// Volumenstrom (Alt) oder Massenstrom aus dem Emissionsbericht (Neu) -- wortgleich mit
// KilnLoadFieldCatalog::BASIS_CONCENTRATION/BASIS_MASS_FLOW im Backend.
export type KilnLoadCalculationBasis = 'concentration' | 'massFlow';
export const CALCULATION_BASIS_CONCENTRATION: KilnLoadCalculationBasis = 'concentration';
export const CALCULATION_BASIS_MASS_FLOW: KilnLoadCalculationBasis = 'massFlow';

/**
 * Liefert ein frisch angelegtes, leeres Rechenblatt (D-14/D-20, erweitert Phase 40 D-06/D-09):
 * alle sechs Betriebsdatenfelder, alle 33 `<key>Value`-Konzentrationsfelder, alle 52
 * `<key>MassFlowCompound`/`<key>MassFlowDirect`-Massenstromfelder und `calculationBasis` --
 * zusammen 92 Schluessel, alle auf `null`. Ausdruecklich `null`, kein anderer Vorbelegungswert --
 * derselbe Grund wie an createEmptyMeasurement() oben: ein Nicht-Null-Default wuerde eine frisch
 * angelegte, sonst leere Struktur faelschlich als "traegt Daten" erscheinen lassen. Anders als bei
 * den Messungen gibt es hier aber ohnehin kein hasAnyData(), das davon abhinge -- die Backend-
 * Entity braucht keins (D-14/D-20, RESEARCH.md Pitfall 3). `calculationBasis: null` bedeutet
 * Konzentration (D-09, wie im Rechner) -- hydrate.ts normalisiert das beim Laden fuer das
 * SegmentedControl in 40-07. KEINE `<key>BelowLimit`-Felder (D-13): da ohnehin alles neu
 * eingegeben wird, traegt der Melder schlicht die Zahl ein, mit der er rechnen will.
 */
export function createEmptyLoadCalculation(): Record<string, string | null> {
  const operatingFields = Object.fromEntries(ALL_KILN_LOAD_OPERATING_KEYS.map((key) => [key, null]));
  const componentFields = Object.fromEntries(
    KILN_LOAD_COMPONENT_KEYS.map((key) => [`${key}Value`, null]),
  );
  const massFlowFields = Object.fromEntries(
    ALL_KILN_LOAD_MASS_FLOW_KEYS.map((key) => [key, null]),
  );
  return { ...operatingFields, ...componentFields, ...massFlowFields, calculationBasis: null };
}

/**
 * Kopfzaehler der Ofenkarte im Reiter Frachtenberechnung (Plan 38.1-02, erweitert 40-06, Sketch
 * 024 `meta()`): zaehlt, wie viele der 33 Komponenten im FORMULARSTAND eine Angabe tragen. Die 7
 * kontinuierlichen Komponenten zaehlen immer ueber ihre Konzentration (`${key}Value`). Die 26
 * diskontinuierlichen zaehlen modusabhaengig ueber `calculationBasis` DESSELBEN loadCalculation-
 * Objekts (D-09): im Massenstrom-Modus ueber "Oe VB ODER Oe DB gefuellt"
 * (`${key}MassFlowCompound`/`${key}MassFlowDirect`), sonst -- also im Konzentrations-Modus UND bei
 * `calculationBasis: null` (D-09: null bedeutet Konzentration) -- weiterhin ueber `${key}Value`.
 * '0'/'0.0' zaehlt als Angabe (die Messung ist ja durchgefuehrt worden), leerer String und
 * `null`/`undefined` nicht. Das ist ausdruecklich KEIN Server-Aggregat: der Server liefert keinen
 * Zaehler, und `result.loads` taugt dafuer nicht, weil es ohne Betriebsdaten leer bleibt, obwohl
 * Konzentrationen erfasst sind. Betriebsdatenfelder (compound-/direct-Praefix der Betriebsdaten-
 * Tabelle) zaehlen nicht mit -- dieselbe Trennung wie im Sketch (`n` vs. `hatOps`).
 */
export function countFilledLoadComponents(
  loadCalculation: Record<string, unknown> | null | undefined,
): number {
  if (!loadCalculation) return 0;

  const hasValue = (value: unknown): boolean => value !== null && value !== undefined && value !== '';
  const isMassFlowMode = loadCalculation.calculationBasis === CALCULATION_BASIS_MASS_FLOW;

  return KILN_LOAD_COMPONENT_KEYS.filter((key) => {
    if (isMassFlowMode && KILN_LOAD_DISCONTINUOUS_KEYS.includes(key)) {
      return hasValue(loadCalculation[massFlowCompoundKey(key)]) || hasValue(loadCalculation[massFlowDirectKey(key)]);
    }
    return hasValue(loadCalculation[`${key}Value`]);
  }).length;
}
